import { useState } from 'react';

export default function FAQPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useState(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const navigate = (path: string) => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE(path);
    }
  };

  const faqCategories = [
    {
      category: 'About Services',
      icon: 'ri-service-line',
      questions: [
        {
          question: 'What types of insurance do you offer?',
          answer: 'I specialize in Life Insurance, Health Insurance, Long-Term Care Insurance, and Medicare Solutions. Each policy is tailored to meet your unique needs and circumstances, ensuring comprehensive coverage for you and your family.'
        },
        {
          question: 'Which states are you licensed to serve?',
          answer: 'I am currently licensed to serve clients in Texas, Louisiana, California, Illinois, and Washington State. If you reside in one of these states, I can help you navigate your insurance options and find the right coverage for your needs.'
        },
        {
          question: 'Do you offer free consultations?',
          answer: 'Yes! I offer complimentary consultations to discuss your insurance needs, answer your questions, and help you understand your options. There\'s no obligation—just an opportunity to learn how I can support you and your family.'
        },
        {
          question: 'How do you charge for your services?',
          answer: 'My services are typically commission-based, meaning you don\'t pay out-of-pocket fees for consultations or policy setup. I work with insurance carriers who compensate me, so you can access expert guidance at no direct cost to you.'
        }
      ]
    },
    {
      category: 'Life Insurance',
      icon: 'ri-shield-heart-line',
      questions: [
        {
          question: 'What is the difference between term and whole life insurance?',
          answer: 'Term life insurance provides coverage for a specific period (10, 20, or 30 years) and is generally more affordable. Whole life insurance offers lifelong coverage with a cash value component that grows over time. The best choice depends on your financial goals and family needs.'
        },
        {
          question: 'How much life insurance do I need?',
          answer: 'The amount of life insurance you need depends on factors like your income, debts, family size, and future expenses (college tuition, mortgage, etc.). A common guideline is 10-12 times your annual income, but I can help you calculate a personalized amount during our consultation.'
        },
        {
          question: 'Can I get life insurance if I have pre-existing health conditions?',
          answer: 'Yes, many people with pre-existing conditions can still qualify for life insurance. Options include simplified issue policies, guaranteed issue policies, or working with carriers that specialize in high-risk cases. I can help you find the right solution.'
        }
      ]
    },
    {
      category: 'Health Insurance',
      icon: 'ri-heart-pulse-line',
      questions: [
        {
          question: 'What is the difference between HMO and PPO plans?',
          answer: 'HMO (Health Maintenance Organization) plans typically require you to choose a primary care physician and get referrals for specialists, but they often have lower premiums. PPO (Preferred Provider Organization) plans offer more flexibility to see any doctor without referrals, but usually have higher premiums.'
        },
        {
          question: 'When can I enroll in health insurance?',
          answer: 'You can enroll during the annual Open Enrollment Period (typically November-December) or during a Special Enrollment Period if you experience a qualifying life event (marriage, birth, job loss, etc.). I can help you understand your enrollment options and deadlines.'
        },
        {
          question: 'What is a deductible and how does it work?',
          answer: 'A deductible is the amount you pay out-of-pocket for healthcare services before your insurance starts covering costs. For example, with a $2,000 deductible, you pay the first $2,000 of covered services, then your insurance kicks in. Lower deductibles mean higher premiums, and vice versa.'
        }
      ]
    },
    {
      category: 'Long-Term Care',
      icon: 'ri-hand-heart-line',
      questions: [
        {
          question: 'What does long-term care insurance cover?',
          answer: 'Long-term care insurance helps cover services like in-home care, assisted living, nursing home care, and adult day care. It\'s designed to help you maintain independence and quality of life when you need assistance with daily activities like bathing, dressing, or eating.'
        },
        {
          question: 'When should I consider purchasing long-term care insurance?',
          answer: 'The ideal time to purchase long-term care insurance is in your 50s or early 60s when you\'re still healthy and premiums are more affordable. Waiting too long can result in higher costs or difficulty qualifying due to health issues.'
        },
        {
          question: 'How much does long-term care insurance cost?',
          answer: 'Costs vary based on your age, health, coverage amount, and policy features. On average, premiums range from $1,500 to $3,500 annually for a 55-year-old. I can provide personalized quotes based on your specific needs and budget.'
        },
        {
          question: 'Does Medicare cover long-term care?',
          answer: 'Medicare provides limited coverage for short-term skilled nursing care or home health services following a hospital stay, but it does not cover long-term custodial care. That\'s why long-term care insurance is so important for comprehensive protection.'
        }
      ]
    },
    {
      category: 'Medicare',
      icon: 'ri-hospital-line',
      questions: [
        {
          question: 'When can I enroll in Medicare?',
          answer: 'You can first enroll during your Initial Enrollment Period, which begins 3 months before your 65th birthday month and ends 3 months after. There\'s also an Annual Enrollment Period (October 15 - December 7) when you can make changes to your coverage.'
        },
        {
          question: 'What is the difference between Medicare Advantage and Medicare Supplement?',
          answer: 'Medicare Advantage (Part C) is an all-in-one alternative to Original Medicare, often including prescription drug coverage. Medicare Supplement (Medigap) works alongside Original Medicare to help cover out-of-pocket costs like deductibles and copays. I can help you determine which is best for you.'
        },
        {
          question: 'Do I need Part D prescription drug coverage?',
          answer: 'If you take prescription medications regularly, Part D coverage is highly recommended. Without it, you could face significant out-of-pocket costs and potential late enrollment penalties if you sign up later.'
        }
      ]
    },
    {
      category: 'Caregiving Support',
      icon: 'ri-heart-add-line',
      questions: [
        {
          question: 'How can you help me as a caregiver?',
          answer: 'As someone who has been a caregiver for over 50 years, I understand the challenges you face. I can help you navigate insurance options, understand benefits, plan for long-term care needs, and connect you with resources to support both you and your loved one.'
        },
        {
          question: 'What resources are available for family caregivers?',
          answer: 'There are many resources available, including respite care programs, caregiver support groups, financial assistance programs, and educational materials. I provide free downloadable guides and can connect you with local and national caregiver resources.'
        },
        {
          question: 'How do I start planning for my parents\' care needs?',
          answer: 'Start by having an open conversation with your parents about their wishes and needs. Assess their current health, financial situation, and living arrangements. I can guide you through evaluating insurance options, long-term care facilities, and creating a comprehensive care plan.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm shadow-md' : 'bg-white shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <button onClick={() => navigate('/')} className="flex items-center cursor-pointer">
              {/* Logo removed */}
            </button>
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => navigate('/')} className="text-sm font-medium text-gray-800 transition-colors hover:text-teal-600 whitespace-nowrap">Home</button>
              <button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('about'), 100); }} className="text-sm font-medium text-gray-800 transition-colors hover:text-teal-600 whitespace-nowrap">About</button>
              <button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('services'), 100); }} className="text-sm font-medium text-gray-800 transition-colors hover:text-teal-600 whitespace-nowrap">Services</button>
              <button onClick={() => navigate('/faq')} className="text-sm font-medium text-teal-600 transition-colors hover:text-teal-700 whitespace-nowrap">FAQ</button>
              <button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('contact'), 100); }} className="bg-teal-600 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap">Contact</button>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2">
              <i className={`ri-menu-line text-2xl text-gray-800`}></i>
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => navigate('/')} className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-50 rounded-lg whitespace-nowrap">Home</button>
              <button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('about'), 100); }} className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-50 rounded-lg whitespace-nowrap">About</button>
              <button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('services'), 100); }} className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-50 rounded-lg whitespace-nowrap">Services</button>
              <button onClick={() => navigate('/faq')} className="block w-full text-left px-4 py-2 text-teal-600 hover:bg-gray-50 rounded-lg whitespace-nowrap">FAQ</button>
              <button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('contact'), 100); }} className="block w-full text-left px-4 py-2 bg-teal-600 text-white hover:bg-teal-700 rounded-lg whitespace-nowrap">Contact</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-question-answer-line"></i>
              Frequently Asked Questions
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-gray-900 mb-6">
              Your Questions, <span className="text-teal-600">Answered</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Find clear, straightforward answers to common questions about insurance, caregiving, and how I can help you and your family navigate life's important decisions.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {faqCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-16 last:mb-0">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl">
                  <i className={`${category.icon} text-2xl text-teal-600`}></i>
                </div>
                <h2 className="text-2xl sm:text-3xl font-serif font-bold text-gray-900">{category.category}</h2>
              </div>
              <div className="space-y-4">
                {category.questions.map((faq, faqIndex) => {
                  const globalIndex = categoryIndex * 100 + faqIndex;
                  const isOpen = openIndex === globalIndex;
                  return (
                    <div key={faqIndex} className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                      <button
                        onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                        className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left cursor-pointer"
                      >
                        <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                        <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                          <i className="ri-arrow-down-s-line text-xl text-teal-600"></i>
                        </div>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
                        <div className="px-6 pb-5">
                          <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-24 bg-gradient-to-r from-teal-600 to-teal-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
            <i className="ri-chat-smile-3-line text-3xl text-white"></i>
          </div>
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white mb-4">Still Have Questions?</h2>
          <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto">
            I'm here to help. Reach out for a free consultation, and let's discuss your unique needs and concerns.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => { navigate('/'); setTimeout(() => scrollToSection('contact'), 100); }}
              className="bg-white text-teal-600 px-8 py-4 rounded-full text-lg font-medium hover:bg-gray-50 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line"></i>
              Send a Message
            </button>
            <a 
              href="tel:+18323622554" 
              className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-white/10 transition-all flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              Call 832-362-2554
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              {/* Logo removed from footer */}
              <p className="text-gray-300 mb-6">Licensed Life &amp; Health Insurance Agent dedicated to helping families navigate care with compassion and clarity.</p>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full hover:bg-white/20 transition-colors">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full hover:bg-white/20 transition-colors">
                  <i className="ri-linkedin-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full hover:bg-white/20 transition-colors">
                  <i className="ri-instagram-line"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li><button onClick={() => navigate('/')} className="text-gray-300 hover:text-white transition-colors whitespace-nowrap">Home</button></li>
                <li><button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('about'), 100); }} className="text-gray-300 hover:text-white transition-colors whitespace-nowrap">About</button></li>
                <li><button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('services'), 100); }} className="text-gray-300 hover:text-white transition-colors whitespace-nowrap">Services</button></li>
                <li><button onClick={() => navigate('/faq')} className="text-gray-300 hover:text-white transition-colors whitespace-nowrap">FAQ</button></li>
                <li><button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('contact'), 100); }} className="text-gray-300 hover:text-white transition-colors whitespace-nowrap">Contact</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Service Areas</h4>
              <ul className="space-y-3">
                <li className="text-gray-300">Texas</li>
                <li className="text-gray-300">Louisiana</li>
                <li className="text-gray-300">California</li>
                <li className="text-gray-300">Illinois</li>
                <li className="text-gray-300">Washington</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Contact Info</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-gray-300">
                  <i className="ri-phone-line"></i>
                  <a href="tel:+18323622554" className="hover:text-white transition-colors">832-362-2554</a>
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <i className="ri-mail-line"></i>
                  <a href="mailto:support@cherylbvictor.com" className="hover:text-white transition-colors">support@cherylbvictor.com</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
              <div className="text-gray-400 text-sm text-center lg:text-left">
                <p>© 2024 Cheryl Victor. All rights reserved.</p>
                <p className="mt-1">Licensed Life &amp; Health Insurance Agent | NPN: 12345678</p>
              </div>
              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
                <a href="#" className="hover:text-white transition-colors whitespace-nowrap">Privacy Policy</a>
                <a href="#" className="hover:text-white transition-colors whitespace-nowrap">Terms of Service</a>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors whitespace-nowrap">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
